import {FormService} from "../../../../umbrella-common/services/form.service";
import {Injectable} from "@angular/core";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {BinaryQuestion, Order, Product, StandardName, TextQuestion} from "../../../../store/state/order.state";
import {UmbrellaValidators} from "../../../../umbrella-common/validation/umbrella-validators";
import {debounceTime, distinctUntilChanged, takeWhile} from "rxjs/operators";
import {AppState} from "../../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {BinaryQuestionData, OrderActions, TextQuestionData} from "../../../../store/actions/order.actions";
import {OutsourcedProcessFormService} from "../outsourced-process/outsourced-process-form.service";
import {BaseOrderFormService} from "../../common/base-order-form.service";
import {List} from "immutable";
import {TranslateService} from "@ngx-translate/core";

@Injectable()
export class ProductAdditionalQuestionsFormService extends FormService implements BaseOrderFormService {

	private textAreaFormControls: string[] = [];

	readonly NOT_COPIABLE_GROUPS = ['POTENTIAL_ASPECTS', 'POTENTIAL_RISKS'];

	private controlNamesInOrder: string[] = [];
	private controlNamePerQuestionGroup: Map<string, string> = new Map<string, string>();

	constructor(private fb: FormBuilder, private store: Store<AppState>, private outsourcedProcessFormService: OutsourcedProcessFormService, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [product, textQuestionsData, textQuestionsForGroups, binaryQuestions]: [Product, TextQuestionData[], TextQuestionData[], BinaryQuestionData[]]) {
		const textQuestions = textQuestionsData.map(tq => tq.question);
		this.initControlNamesInOrder(product, textQuestions, binaryQuestions);
		const formGroup = new FormGroup({});
		textQuestions.forEach((textQuestion, index) => this.addTextQuestionFormControl(product, textQuestion, formGroup, index));
		binaryQuestions.forEach((binaryQuestion, index) => this.addBinaryQuestionFormControl(product, binaryQuestion, formGroup, index));

		this.initializeQuestionsMap(textQuestionsData);
		return formGroup;
	}

	private initializeQuestionsMap(textQuestionsData: TextQuestionData[]) {
		textQuestionsData.map((question, index) => {
			this.controlNamePerQuestionGroup.set(`text-question-${index}`, question.group);
		});
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	override triggerValidations() {
		super.triggerValidations();
		this.outsourcedProcessFormService.triggerValidations();
	}

	protected override tryCopyAnswer(controlName: string, srcForm: string, dstForm: string, isCheckbox: boolean = false) {
		if (!this.NOT_COPIABLE_GROUPS.includes(this.controlNamePerQuestionGroup.get(controlName))) {
			super.tryCopyAnswer(controlName, srcForm, dstForm, isCheckbox);
		}
	}

	getTextQuestionControlsNames(formKey: string): string[] {
		return Object.keys(this.getForm(formKey).controls).filter(controlName => controlName.includes("text-question"));
	}

	getTextQuestionIndex(textFormControlName: string, formKey: string): number {
		return Number(textFormControlName.replace('text-question-', '').replace(formKey, ''));
	}

	getBinaryQuestionControlsNames(formKey: string): string[] {
		return Object.keys(this.getForm(formKey).controls).filter(controlName => controlName.includes("binary-question-radio"));
	}

	getBinaryQuestionIndex(textFormControlName: string, formKey: string): number {
		return Number(textFormControlName.replace('binary-question-radio-', '').replace(formKey, ''));
	}

	getBinaryQuestionDescription(formKey: string, index: number): string {
		return this.getValueOrNull(formKey, 'binary-question-description-' + index);
	}

	private addTextQuestionFormControl(product: Product, textQuestion: string, formGroup: FormGroup, index: number) {
		const existingAnswer = this.answerForTextQuestion(product, textQuestion);
		const formControl = this.fb.control(existingAnswer, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]);

		const controlName = this.textQuestionControlName(index, product.standard.name);
		formGroup.addControl(controlName, formControl);
		this.textAreaFormControls.push(controlName);
		this.initTextQuestionControl(formControl, controlName, index, product.standard.name);
	}

	private answerForTextQuestion(product: Product, textQuestion: string): string | null {
		const question = product.additionalQuestions.textQuestions.find(q => q.question === textQuestion);
		return !!question ? question.answer : null;
	}

	private textQuestionControlName(questionIndex: number, formKey: string) {
		return `text-question-${questionIndex}${formKey}`;
	}

	private initTextQuestionControl(formControl: FormControl, controlName: string, questionIndex: number, standardName: StandardName) {
		formControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standardName)), debounceTime(600), distinctUntilChanged())
			.subscribe(answer => this.store.dispatch(OrderActions.ANSWER_TEXT_QUESTION({
				standardName,
				questionIndex,
				answer
			})));
	}

	private addBinaryQuestionFormControl(product: Product, binaryQuestion: BinaryQuestionData, formGroup: FormGroup, index: number) {
		const existingAnswer = this.answerForBinaryQuestion(product, binaryQuestion.question);
		const radioFormControl = this.fb.control(existingAnswer, [Validators.required]);
		formGroup.addControl(this.binaryQuestionRadioControlName(index, product.standard.name), radioFormControl);
		this.initBinaryQuestionRadioControl(product, formGroup, radioFormControl, binaryQuestion, index);

		if (existingAnswer === binaryQuestion.commentMandatoryWithAnswerValueEqualTo) {
			this.addBinaryQuestionTextAreaControl(product, binaryQuestion.question, formGroup, binaryQuestion.type, index);
		}
	}

	private answerForBinaryQuestion(product: Product, binaryQuestion: string): boolean | null {
		const question = product.additionalQuestions.binaryQuestions.find(q => q.question === binaryQuestion);
		return !!question ? question.answer : null;
	}

	private binaryQuestionRadioControlName(questionIndex: number, formKey: string): string {
		return `binary-question-radio-${questionIndex}${formKey}`;
	}

	private initBinaryQuestionRadioControl(product: Product, formGroup: FormGroup, formControl: FormControl, binaryQuestion: BinaryQuestionData, questionIndex: number) {
		formControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(answer => {
			this.store.dispatch(OrderActions.ANSWER_BINARY_QUESTION({standardName: product.standard.name, binaryQuestion, answer}));

			if (answer === binaryQuestion.commentMandatoryWithAnswerValueEqualTo) {
				this.addBinaryQuestionTextAreaControl(product, binaryQuestion.question, formGroup, binaryQuestion.type, questionIndex);
			} else {
				const controlName = this.binaryQuestionDescriptionControlName(questionIndex, product.standard.name);
				this.textAreaFormControls = this.textAreaFormControls.filter(c => c != controlName);
				formGroup.removeControl(controlName);
			}
		});
	}

	private addBinaryQuestionTextAreaControl(product: Product, binaryQuestion: string, formGroup: FormGroup, questionType: string, index: number) {
		let description = this.descriptionForBinaryQuestion(product, binaryQuestion);
		const textAreaFormControl = this.fb.control(description, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]);
		const controlName = this.binaryQuestionDescriptionControlName(index, product.standard.name);
		this.textAreaFormControls.push(controlName);
		formGroup.addControl(controlName, textAreaFormControl);
		this.initBinaryQuestionTextAreaControl(textAreaFormControl, controlName, questionType, index, product.standard.name);
	}

	private descriptionForBinaryQuestion(product: Product, binaryQuestion: string): string {
		const question = product.additionalQuestions.binaryQuestions.find(q => q.question === binaryQuestion);
		return !!question ? question.description : null;
	}

	private binaryQuestionDescriptionControlName(questionIndex: number, formKey: string): string {
		return `binary-question-description-${questionIndex}${formKey}`;
	}

	private initBinaryQuestionTextAreaControl(formControl: FormControl, controlName: string, questionType: string, questionIndex: number, standardName: StandardName) {
		formControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(standardName)), debounceTime(600), distinctUntilChanged())
			.subscribe(description => this.store.dispatch(OrderActions.ADD_BINARY_QUESTION_DESCRIPTION({
				standardName,
				questionType,
				description
			})));
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().forEach(formKey => this.fillProductInfo(order.products.find(p => p.standard.name === formKey), order));
		return order;
	}

	private fillProductInfo(product: Product, order: Order) {
		if (product.standard.name == StandardName.ISO_27701) {
			let iso27001TextQuestions = this.getTextQuestions(StandardName.ISO_27001, order);
			let iso27701TextQuestions = this.getTextQuestions(StandardName.ISO_27701, order)
				.filter(tq => !iso27001TextQuestions.find(q => q.type == tq.type));
			product.additionalQuestions.textQuestions = iso27001TextQuestions.concat(iso27701TextQuestions);

			let iso27001BinaryQuestions = this.getBinaryQuestions(StandardName.ISO_27001, order);
			let iso27701BinaryQuestions = this.getBinaryQuestions(StandardName.ISO_27701, order)
				.filter(bq => bq != undefined ? !iso27001BinaryQuestions.find(q => q.type == bq.type) : false);
			product.additionalQuestions.binaryQuestions = iso27701BinaryQuestions.concat(iso27001BinaryQuestions);
		} else {
			product.additionalQuestions.textQuestions = this.getTextQuestions(product.standard.name, order);
			product.additionalQuestions.binaryQuestions = this.getBinaryQuestions(product.standard.name, order);
		}
	}

	private getTextQuestions(formKey: string, order: Order): TextQuestion[] {
		return this.getTextQuestionControlsNames(formKey).map(controlName => {
			const textQuestion = this.getTextQuestionFromOrder(order, formKey, this.getTextQuestionIndex(controlName, formKey));
			return {
				type: textQuestion.type,
				question: textQuestion.question,
				group: textQuestion.group,
				answer: this.getFormControl(formKey, controlName).value
			};
		});
	}

	private getBinaryQuestions(formKey: string, order: Order): BinaryQuestion[] {
		if (formKey == StandardName.ISO_27701) {
			return this.getBinaryQuestionsFromOrder(order, StandardName.ISO_27701);
		}

		return this.getBinaryQuestionControlsNames(formKey).map(controlName => {
			const index = this.getBinaryQuestionIndex(controlName, formKey);
			const binaryQuestion = this.getBinaryQuestionFromOrder(order, formKey, index);
			return {
				type: binaryQuestion.type,
				question: binaryQuestion.question,
				answer: this.getValueOrNull(formKey, controlName),
				description: this.getBinaryQuestionDescription(formKey, index),
				commentMandatoryWithAnswerValueEqualTo: binaryQuestion.commentMandatoryWithAnswerValueEqualTo
			} as BinaryQuestion;
		});
	}

	private getTextQuestionFromOrder(order: Order, formKey: string, index: number): TextQuestion {
		return order.products.find(product => product.standard.name == formKey).additionalQuestions.textQuestions[index];
	}

	private getBinaryQuestionFromOrder(order: Order, formKey: string, index: number): BinaryQuestion {
		return order.products.find(product => product.standard.name == formKey).additionalQuestions.binaryQuestions[index];
	}

	private getBinaryQuestionsFromOrder(order: Order, standardName: StandardName): BinaryQuestion[] {
		return order.products.find(product => product.standard.name == standardName).additionalQuestions.binaryQuestions;
	}

	private initControlNamesInOrder(product: Product, textQuestions: string[], binaryQuestions: BinaryQuestionData[]) {
		const binaryQuestionControls = List(binaryQuestions).flatMap((bq, i) => {
			return [this.binaryQuestionRadioControlName(i, product.standard.name), this.binaryQuestionDescriptionControlName(i, product.standard.name)];
		}).toArray();

		this.controlNamesInOrder = this.controlNamesInOrder.concat(binaryQuestionControls)
			.concat(textQuestions.map((tq, i) => this.textQuestionControlName(i, product.standard.name)));
	}
}
